import type { EntityId, Entity } from '../sim/entity';
import type { World } from '../sim/world';
import type { Direction } from '../sim/direction';
import type { GameRenderer } from '../render/gameRenderer';
import type { GameClient } from '../net/gameClient';
import type {
  ActionAnimationMessage,
  ArenaDraftMessage,
  ArenaFxMessage,
  ArenaRecordMessage,
  ArenaResultsMessage,
  ArenaRunStatusMessage,
  ArenaWarningMessage,
  ArenaHistoryPageMessage,
  BloodFxMessage,
  CombatTextMessage,
  DungeonRunStatusMessage,
  DungeonCastResultMessage,
  SoulstoneRosterMessage,
  CardStateMessage,
  CardDuelPromptMessage,
  CardDuelListingsMessage,
  CardDuelListingAcceptedMessage,
  CardDuelStateMessage,
  DiceWagerPromptMessage,
  DuelCombatMessage,
  DuelPromptMessage,
  SpherePromptMessage,
  SphereStateMessage,
  SphereEventMessage,
  SphereCommandResultMessage,
  JusticeFxMessage,
  RatSwarmStatusMessage,
  SpeechMessage,
  ChatHistoryRemoveMessage,
  StormStatusMessage,
  StormTelegraphMessage,
  DungeonTelegraphMessage,
  WardrobePair,
  WardrobeFavoritesStateMessage,
  WireDeckCard,
  WireCardLink,
} from '../net/protocol';
import type { LeaderboardFetcher } from '../net/leaderboardApi';
import type { AudioManager } from '../audio/audioManager';
import type { MusicPlayer } from '../audio/musicPlayer';
import type { Action, BindingTable } from '../input/keybindings';
import type { SplashIdentity } from '../splash';
import type { DuelUi } from '../duelUi';
import type { IgnoreListHandle } from '../ignoreList';
import type { ToolbarGroup } from '../toolbar';
import type { BodyFrames, EquipFrames } from '../render/playerArt';
import type { HueTable } from '../render/hueTable';
import type { HuePalette } from '../render/huePalette';
import type { SpriteAtlasStore } from '../render/spriteAtlasStore';
import type { StaticPlacement } from '../render/staticsLayer';
import { openContextMenu } from '../ignoreMenu';

/**
 * The client feature-installer seams (Chassis Slice G, PRD #534 D6, ADR-0268).
 *
 * `main()` used to be one ~4,000-line closure that smeared every feature across its
 * lexical scope. It is now a *bootstrap* - app/assets/socket/movement - that builds
 * one {@link FeatureContext} and hands it to one `installX(ctx)` per feature
 * (duel, dice, storm, swarm, arena, ...). Everything an installer may touch is a
 * named seam on the context; nothing reaches back into `main()`'s scope, which is
 * what makes each installer a plain module unit-testable against a fake context.
 *
 * Three registries carry the fan-out that used to be inline in `main()`:
 *
 * - {@link ClientEventHub}: the per-feature `GameClient` callbacks. The bootstrap
 *   wires each fresh connection's `client.onX` to `hub.emit('x', ...)` once;
 *   installers register any number of handlers per event. The `connection` event
 *   fires at the top of every connect attempt - it replaces the per-`connect()`
 *   closure variables (swarmActive / stormActive / arenaActive edge latches), so a
 *   reconnect still resets them exactly as the old closures did.
 * - {@link ContextMenuRegistry}: the long-press context menu is contribution-based
 *   (each installer contributes rows) instead of one inline `onSelect` chain; see
 *   its docs for how today's ordering/gating is preserved.
 * - action / chat-command registries: keybinding handlers and client-side chat
 *   interceptors, each a registration instead of an entry in a hand-maintained map.
 */

/* ------------------------------------------------------------------------- *
 * Client event hub
 * ------------------------------------------------------------------------- */

/**
 * Every event installers can hear, with its handler argument tuple. These mirror
 * the `GameClient.onX` callbacks one-for-one (plus the bootstrap-raised
 * `connection` / `rendererBuilt`-style hooks that live on the context itself).
 */
export interface ClientEvents {
  /**
   * A fresh gameplay socket is being opened (fired at the top of every connect
   * attempt, before login). Installers reset their per-connection edge latches
   * here - the replacement for declaring them inside the old `connect()` closure.
   */
  connection: [];
  arenaHistoryPage: [msg: ArenaHistoryPageMessage];
  /** An accepted (re)login: the player is (back) in the world. The reconnect/PR #243 reset hook. */
  welcome: [playerId: EntityId, world: World];
  /** A fresh read-model world was adopted (the bootstrap has already updated its own seams). */
  snapshot: [world: World];
  speech: [speech: SpeechMessage];
  chatHistoryRemove: [message: ChatHistoryRemoveMessage];
  actionAnimation: [msg: ActionAnimationMessage];
  combatText: [msg: CombatTextMessage];
  dungeonRunStatus: [msg: DungeonRunStatusMessage];
  dungeonCastResult: [msg: DungeonCastResultMessage];
  dungeonTelegraph: [msg: DungeonTelegraphMessage];
  soulstoneRoster: [msg: SoulstoneRosterMessage];
  ratSwarmStatus: [msg: RatSwarmStatusMessage];
  stormStatus: [msg: StormStatusMessage];
  stormTelegraph: [msg: StormTelegraphMessage];
  justiceFx: [msg: JusticeFxMessage];
  bloodFx: [msg: BloodFxMessage];
  clearCorpses: [];
  goldUpdate: [gold: number, durable: boolean];
  duelPrompt: [msg: DuelPromptMessage];
  duelCombat: [msg: DuelCombatMessage];
  spherePrompt: [msg: SpherePromptMessage];
  sphereState: [msg: SphereStateMessage];
  sphereEvent: [msg: SphereEventMessage];
  sphereCommandResult: [msg: SphereCommandResultMessage];
  diceWagerPrompt: [msg: DiceWagerPromptMessage];
  cardDuelPrompt: [msg: CardDuelPromptMessage];
  cardDuelListings: [msg: CardDuelListingsMessage];
  cardDuelListingAccepted: [msg: CardDuelListingAcceptedMessage];
  cardDuelState: [msg: CardDuelStateMessage];
  arenaRunStatus: [msg: ArenaRunStatusMessage];
  arenaDraft: [msg: ArenaDraftMessage];
  arenaResults: [msg: ArenaResultsMessage];
  arenaRecord: [msg: ArenaRecordMessage];
  arenaFx: [msg: ArenaFxMessage];
  arenaWarning: [msg: ArenaWarningMessage];
  onlineTime: [totalOnlineMs: number];
  collection: [unlocked: WardrobePair[]];
  cardState: [msg: CardStateMessage];
  cardsEnabled: [enabled: boolean];
  packOpened: [cards: WireDeckCard[], duplicateGold: number];
  /** Client-local request from the Binder to insert a card into the composer. */
  cardShare: [card: WireCardLink & { name: string }];
  /** Client-local request from a journal link to open the card viewer. */
  cardView: [card: WireCardLink];
  /**
   * Client-local request to open the Wardrobe (the paperdoll's self-only `Dress`
   * action, PRD #629): the wardrobe installer owns the handle and listens; the
   * paperdoll installer emits.
   */
  openWardrobe: [];
  autoEquipState: [enabled: boolean];
  wardrobeFavoritesState: [state: WardrobeFavoritesStateMessage];
  isAdmin: [admin: boolean];
}

/**
 * The many-handlers-per-event dispatch behind the one-callback-per-event
 * `GameClient`. Handlers run in registration (= install) order; every registered
 * handler runs (there is no consume/stop), so two features listening to the same
 * message stay independent - exactly like the old single inline handler that did
 * both things in sequence.
 */
export class ClientEventHub {
  private handlers = new Map<keyof ClientEvents, ((...args: never[]) => void)[]>();

  on<K extends keyof ClientEvents>(event: K, fn: (...args: ClientEvents[K]) => void): void {
    const list = this.handlers.get(event) ?? [];
    list.push(fn as (...args: never[]) => void);
    this.handlers.set(event, list);
  }

  emit<K extends keyof ClientEvents>(event: K, ...args: ClientEvents[K]): void {
    const list = this.handlers.get(event);
    if (!list) return;
    for (const fn of list) (fn as (...args: ClientEvents[K]) => void)(...args);
  }
}

/* ------------------------------------------------------------------------- *
 * Context-menu contributions
 * ------------------------------------------------------------------------- */

/** One row of the in-world long-press context menu. */
export interface MenuItem {
  label: string;
  onSelect: () => void;
}

/** Where a long-press landed: canvas px for hit-testing, viewport px for the popover anchor. */
export interface MenuRequest {
  /** Canvas-pixel press position, for the renderer's pick* hit tests. */
  cx: number;
  cy: number;
  /** Viewport (client) position the menu anchors at. */
  clientX: number;
  clientY: number;
}

/**
 * A non-character surface's menu (the dice-and-cup, a treasure chest, a moongate).
 * Providers hit-test the press themselves (via the renderer seam they closed over)
 * and return rows iff their surface is under it; tried in registration order,
 * before the character pool - a cup/chest/gate is never a mobile, so its menu
 * wins its hit exactly as the old inline chain's early returns did.
 */
export type MenuSurfaceProvider = (req: MenuRequest) => MenuItem[] | null;

/** The picked character under a long-press (never the local player, never nameless). */
export interface MenuTarget {
  id: EntityId;
  entity: Entity;
  /** The renderable's name, trimmed. */
  name: string;
}

/**
 * An entity provider's contribution. `exclusive` claims the whole menu (the Arena
 * Master's "Join the arena", the Bank Escort's transaction rows - entities whose
 * menu must never grow PvP rows); the first exclusive contribution in registration
 * order wins and no later provider is consulted, mirroring the old chain's early
 * returns. Non-exclusive rows accumulate in registration order (dice's "Roll the
 * dice" before duel's "Duel <name>" - install order pins it).
 */
export interface EntityMenuContribution {
  items: MenuItem[];
  exclusive?: boolean;
}

export type EntityMenuProvider = (
  target: MenuTarget,
  req: MenuRequest,
) => EntityMenuContribution | null;

/** The narrow picking surface {@link ContextMenuRegistry.open} needs from the renderer. */
export interface MenuPickSource {
  pickEntity(cx: number, cy: number): EntityId | undefined;
}

/** The seams the menu dispatcher resolves at open time (getters - renderer/world churn). */
export interface ContextMenuDeps {
  renderer(): MenuPickSource | undefined;
  world(): World | undefined;
  playerId(): EntityId | undefined;
  /** The popover opener; defaults to the shared {@link openContextMenu}. Injectable for tests. */
  show?: (opts: { x: number; y: number; items: MenuItem[] }) => void;
}

/**
 * The contribution-based context menu (D6): installers contribute surface and
 * entity providers; {@link open} replays today's dispatch order - surfaces first
 * (dice cup / chest / gate), then the picked character (skipping self and nameless
 * mobiles, so a giant rat or empty ground opens nothing), first exclusive
 * contribution wins, else the accumulated rows. Returns `true` iff a menu opened,
 * which is what the long-press driver latches `suppressClick` on.
 */
export class ContextMenuRegistry {
  private surfaces: MenuSurfaceProvider[] = [];
  private entityProviders: EntityMenuProvider[] = [];

  constructor(private deps: ContextMenuDeps) {}

  addSurface(provider: MenuSurfaceProvider): void {
    this.surfaces.push(provider);
  }

  addEntityMenu(provider: EntityMenuProvider): void {
    this.entityProviders.push(provider);
  }

  /**
   * Resolve the rows a picked character would get, without opening anything -
   * the pure decision, exposed for installer tests.
   */
  entityItems(target: MenuTarget, req: MenuRequest): MenuItem[] {
    const items: MenuItem[] = [];
    for (const provider of this.entityProviders) {
      const contribution = provider(target, req);
      if (!contribution) continue;
      if (contribution.exclusive) return contribution.items;
      items.push(...contribution.items);
    }
    return items;
  }

  /** Open the context menu for whatever sits under the press. True iff a menu opened. */
  open(req: MenuRequest): boolean {
    const show = this.deps.show ?? openContextMenu;
    for (const surface of this.surfaces) {
      const items = surface(req);
      if (items && items.length > 0) {
        show({ x: req.clientX, y: req.clientY, items });
        return true;
      }
    }
    const renderer = this.deps.renderer();
    if (!renderer) return false;
    const id = renderer.pickEntity(req.cx, req.cy);
    if (id === undefined || id === this.deps.playerId()) return false;
    const entity = this.deps.world()?.get(id);
    const name = entity?.renderable?.name;
    // A nameless mobile (e.g. a giant rat) or a stale pick opens nothing - the
    // long-press falls through to the normal action, exactly as before.
    if (!entity || !name) return false;
    const items = this.entityItems({ id, entity, name: name.trim() }, req);
    if (items.length === 0) return false;
    show({ x: req.clientX, y: req.clientY, items });
    return true;
  }
}

/* ------------------------------------------------------------------------- *
 * Action / chat-command registries
 * ------------------------------------------------------------------------- */

/** A keybinding action's handler: `down` on the matched keydown, `up` on release (hold-style). */
export interface ActionHandler {
  down: () => void;
  up?: () => void;
}

/**
 * The keybinding dispatch table as a registry: the bootstrap registers the
 * movement-core actions (war-mode, all-names, moonwalk, admin-teleport) and each
 * installer registers its own (fire-bow, bow/salute, say-last). An action nobody
 * registered is a safe no-op on dispatch.
 */
export class ActionRegistry {
  private handlers = new Map<Action, ActionHandler>();

  register(action: Action, handler: ActionHandler): void {
    this.handlers.set(action, handler);
  }

  get(action: Action): ActionHandler | undefined {
    return this.handlers.get(action);
  }
}

/**
 * A client-side chat interceptor: sees each line the player submits *before* it is
 * spoken; returns `true` to consume it (a slash/bracket command - the line never
 * broadcasts and never counts as "last said") or `false` to let it fall through.
 * An interceptor may act without consuming (the spoken "I wish to duel" arms the
 * duel cursor *and* still broadcasts). Run in registration order; first consume wins.
 */
export type ChatInterceptor = (text: string) => boolean;

export class ChatCommandRegistry {
  private interceptors: ChatInterceptor[] = [];

  intercept(fn: ChatInterceptor): void {
    this.interceptors.push(fn);
  }

  /** True iff some interceptor consumed the line (so it must not broadcast). */
  handle(text: string): boolean {
    for (const fn of this.interceptors) {
      if (fn(text)) return true;
    }
    return false;
  }
}

/* ------------------------------------------------------------------------- *
 * Shared cross-feature mode/state
 * ------------------------------------------------------------------------- */

/**
 * What an armed blue target cursor is *for* (Dice Wager, ADR-0154; `/ignore`,
 * ADR-0185): the one cursor serves a duel pick, a dice-wager pick, and an
 * `/ignore` squelch pick; the purpose routes the pick and selects the hover filter.
 */
export type TargetPurpose = 'duel' | 'sphere' | 'dice' | 'ignore';

/**
 * The cross-feature couplings that must survive the installer split explicitly
 * (PRD #534 D6). Owned by the bootstrap's movement/cursor core (which implements
 * the arming/cursor halves) with a few slots installers provide/consume across
 * feature lines - each documented at its member.
 */
export interface SharedState {
  /**
   * Arm the shared blue player-target cursor for a purpose (duel call-out, dice
   * cup menu, `/ignore`). The bootstrap swaps the cursor art, arms the renderer's
   * hover/click wiring, and drops war mode / the admin teleport cursor - the
   * mutual exclusions the old inline `armTargeting` kept.
   */
  armTargeting(purpose: TargetPurpose): void;
  /** Disarm the target cursor (after a pick, on cancel, or when Tab flips war). */
  disarmTargeting(): void;
  /** The purpose the cursor is currently armed for (the duel default while disarmed). */
  targetPurpose(): TargetPurpose;
  /**
   * Route a completed target pick: the installer that arms a purpose registers
   * what a pick does (duel -> wager prompt, dice -> dice prompt, ignore -> squelch).
   * The bootstrap disarms first, then dispatches - one registered handler per purpose.
   */
  onTargetPick(purpose: TargetPurpose, fn: (target: EntityId) => void): void;
  /**
   * Replace the hover/pick eligibility filter for a purpose (the ignore cursor's
   * not-range-limited `isIgnorableTarget`, ADR-0185). Duel/dice keep the default
   * range-limited PvP match filter.
   */
  setTargetFilter(purpose: TargetPurpose, fn: (id: EntityId) => boolean): void;

  /**
   * The owner-only durable-gold mirror (written by the HUD installer off
   * GoldUpdate pushes, read by the duel/dice wager prompts): a reserved balance is
   * spendable on a stake; an anonymous session (durable false) is honor-only.
   */
  gold(): { amount: number; durable: boolean };
  setGold(amount: number, durable: boolean): void;

  /** The one duel/dice prompt window (the dice wager reuses it via `matchNoun`, ADR-0154). */
  duelUi: DuelUi;

  /** The per-viewer ignore/squelch list (chat suppression, Settings panel, `/ignore`). */
  ignoreList: IgnoreListHandle;

  /**
   * Rats whose `die` one-shot has played but whose corpse still lingers in the
   * snapshot (~1s): untargetable for new pursuits, unhighlightable. The swarm
   * installer adds on the die clip and prunes against each snapshot; the
   * movement core's pursuit targeting reads it.
   */
  dyingRats: Set<EntityId>;
  /** The pursued rat died (someone won the race): cancel the chase on the spot. */
  ratDied(rat: EntityId): void;

  /**
   * The local player entered/left a live duel's combat stance (ADR-0131): the
   * movement core forces the war-red cursor for the fight's duration (outranking
   * the Tab war-mode toggle) and reverts on the drop. The duel installer detects
   * the edge off the replicated stance and drives this plus the music override.
   */
  setDuelCombat(inCombat: boolean): void;

  /** The reveal-only admin flag from the Welcome (gates UI only; the server re-authorizes). */
  isAdminViewer(): boolean;
  /** Mirror the admin no-clip toggle into movement prediction + send it up the socket. */
  setNoClip(enabled: boolean): void;
  /** Arm / disarm the admin teleport tile cursor (movement-core owned, ADR-0183). */
  armTeleport(): void;
  disarmTeleport(): void;

  /**
   * The bottom-centre band coupling (PRD #334 follow-up): the Arena run HUD
   * suppresses the town progression strip while a run is live. The HUD installer
   * provides the sink; the arena installer drives it. A no-op until provided.
   */
  setHudSuppressed(suppressed: boolean): void;
  provideHudSuppression(fn: (suppressed: boolean) => void): void;

  /**
   * Whether the chat input has focus (typing halts movement). The chat installer
   * provides it; the bootstrap's movement ticker polls it. False until provided.
   */
  chatInputFocused(): boolean;
  provideChatFocus(fn: () => boolean): void;

  /** Mirror a server-authoritative cast turn onto the prediction walker (ADR-0192). */
  faceCast(dir: Direction): void;
  /** Present a shared-planner dungeon Charge through the local prediction walker. */
  rushDungeon(end: { x: number; y: number }, dir: Direction, durationMs: number): void;
  /** Current discrete local movement pose, which may lead the replicated snapshot. */
  localMovementPose(): {
    x: number;
    y: number;
    z: number;
    facing: Direction;
  } | undefined;
  /** Replay one authoritative dungeon movement status through local prediction. */
  observeDungeonMovement(
    movement: NonNullable<DungeonRunStatusMessage['movement']>,
  ): void;
  /** Ordered intent time included with a moving Charge request. */
  dungeonMovementCheckpoint(): {
    seq: number;
    elapsedMs: number;
  } | undefined;
}

/* ------------------------------------------------------------------------- *
 * The context
 * ------------------------------------------------------------------------- */

/** The loaded art seams installers with preview surfaces need (wardrobe, notifications, chat hues). */
export interface FeatureAssets {
  bodyFrames: BodyFrames;
  equipFrames: EquipFrames;
  hueTable: HueTable;
  huePalette: HuePalette;
  atlasStore: SpriteAtlasStore;
}

/** What an {@link onRendererBuilt} hook gets besides the fresh renderer. */
export interface RendererExtras {
  /** Complete authored static placement data, independent of viewport sprites. */
  statics: readonly StaticPlacement[];
}

/**
 * Everything a feature installer may touch (PRD #534 D6): the client/renderer/world
 * seams as *getters* (connection, scene swaps, and reconnects churn the underlying
 * objects), the shared registries, and the explicit cross-feature state. Installers
 * never see `main()`'s lexical scope.
 */
export interface FeatureContext {
  /** The page document - installers mount their DOM through their own init modules. */
  ui: Document;
  /** The game canvas (the death cinematic's grayscale filter target). */
  canvas: HTMLCanvasElement;
  audio: AudioManager;
  music: MusicPlayer;
  assets: FeatureAssets;
  events: ClientEventHub;
  menu: ContextMenuRegistry;
  actions: ActionRegistry;
  chatCommands: ChatCommandRegistry;
  /** The top-right toolbar's one-panel-open-at-a-time group (ADR-0133). */
  toolbar: ToolbarGroup;
  fetchLeaderboardPage: LeaderboardFetcher;

  /** The live, accepted connection (undefined pre-entry / during an outage). */
  client(): GameClient | undefined;
  /** The local player's entity id (undefined pre-entry). */
  playerId(): EntityId | undefined;
  /** The latest read-model world. */
  world(): World | undefined;
  /** The current renderer (rebuilt on scene swaps - never cache it). */
  renderer(): GameRenderer | undefined;
  /**
   * The live keybinding table (a Settings rebind applies without a reload). For
   * *display* - e.g. the sphere hotbar's key badges; dispatch stays `main.ts`'s
   * single hotkey path through the {@link ActionRegistry}.
   */
  bindings(): BindingTable;
  /** The display name typed on the splash (current across retries). */
  viewerName(): string;
  /** The last identity submitted through connect (sex/hue for own-body cues). */
  identity(): SplashIdentity | undefined;

  /**
   * Run every render-loop frame, after the world sync.
   * A time-sensitive hook supplies an activity predicate so cadence cannot
   * throttle while its animation is visibly changing.
   */
  onFrame(fn: (deltaMS: number) => void, requiresActivePresentation?: () => boolean): void;
  /**
   * Run whenever a renderer is (re)built - login, reconnect, arena scene swap -
   * so per-sprite/per-scene wiring (double-click handlers, billboard clicks)
   * re-attaches to the fresh scene exactly as the old inline buildRenderer did.
   */
  onRendererBuilt(fn: (renderer: GameRenderer, extras: RendererExtras) => void): void;

  state: SharedState;
}
